import { StateField, StateEffect, type Extension, type Range } from '@codemirror/state';
import { Decoration, type DecorationSet, EditorView } from '@codemirror/view';
import { tokens } from '@coreai/fluentui-react';

/**
 * Effect to set the highlighted node range
 */
export const setNodeHighlight = StateEffect.define<{
  nameFrom: number;
  nameTo: number;
  blockFrom: number;
  blockTo: number;
} | null>();

/**
 * Decoration for the node name (the key in JSON)
 */
const nodeNameDecoration = Decoration.mark({
  class: 'cm-node-name-highlight',
});

/**
 * Decoration for the node block lines
 */
const nodeBlockLineDecoration = Decoration.line({
  class: 'cm-node-block-highlight',
});

/**
 * State field that tracks the highlighted node decorations
 */
const nodeHighlightField = StateField.define<DecorationSet>({
  create() {
    return Decoration.none;
  },
  update(decorations, tr) {
    // Check for highlight effect
    for (const effect of tr.effects) {
      if (effect.is(setNodeHighlight)) {
        if (effect.value === null) {
          return Decoration.none;
        }

        const { nameFrom, nameTo, blockFrom, blockTo } = effect.value;
        const decorationRanges: Range<Decoration>[] = [];

        // Add name highlight decoration
        decorationRanges.push(nodeNameDecoration.range(nameFrom, nameTo));

        // Add line decorations for all lines in the block
        const doc = tr.state.doc;
        const startLine = doc.lineAt(blockFrom).number;
        const endLine = doc.lineAt(blockTo).number;

        for (let lineNum = startLine; lineNum <= endLine; lineNum++) {
          const line = doc.line(lineNum);
          decorationRanges.push(nodeBlockLineDecoration.range(line.from));
        }

        // Sort decorations by position (required by CodeMirror)
        decorationRanges.sort((a, b) => a.from - b.from);

        return Decoration.set(decorationRanges);
      }
    }

    // Map decorations through document changes
    return decorations.map(tr.changes);
  },
  provide: (field) => EditorView.decorations.from(field),
});

/**
 * Theme styles for node highlighting
 */
const nodeHighlightTheme = EditorView.baseTheme({
  '.cm-node-name-highlight': {
    backgroundColor: 'var(--colorBrandBackground2)',
    borderRadius: tokens.borderRadiusSmall,
    paddingBlock: 0,
    paddingInline: tokens.spacingHorizontalXXS,
    fontWeight: tokens.fontWeightSemibold,
  },
  '.cm-node-block-highlight': {
    backgroundColor: 'var(--colorNeutralBackground1Hover)',
  },
});

/**
 * Creates the node highlight extension
 */
export const createNodeHighlightExtension = (): Extension => {
  return [nodeHighlightField, nodeHighlightTheme];
};
